import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Radio,
  IconButton,
  Button,
  Divider,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Add, DeleteOutline } from "@mui/icons-material";
import { useState, useEffect } from "react";
import TextInput from "../../../../../components/textinput/textinput";
import { useDispatch, useSelector } from "react-redux";
import {
  getCountriesListRequest,
  getStatesByCountryRequest,
  resetLocationsData,
} from "../../../../../redux/locations/slice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Loading from "../../../../../components/loading/loading";
import { normalizePropertyContactPhoneType } from "../../../../../utils/Formatter";
import InputNumber from "../../../../../components/input-number/input-number";
import OptionalPropertyDetails from "../../../add-order/property-section/optional-property-details";

interface EditPropertyDetailsProps {
  mode: "add" | "edit";
  formik: any;
  loading?: boolean;
  clientId?: string;
  disabled?: boolean;
  /** When true, only show design fields: Loan #, Property Name, Address, Property Contact. Other fields stay in state. */
  simplifiedView?: boolean;
}

interface PropertyContact {
  id?: string;
  name: string;
  email: string;
  phone: string;
  phoneType: string;
  isPrimary: boolean;
}

interface OptionalPropertyDetailsType {
  type: string;
  description: string;
}

const labelAboveSx = { fontSize: 12, color: "rgba(0, 0, 0, 0.6)", mb: 0.5 };
const inputBgSx = { "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } };

export default function EditPropertyDetails({
  formik,
  clientId = "",
  disabled = false,
  simplifiedView = false,
}: EditPropertyDetailsProps) {
  const [contacts, setContacts] = useState<PropertyContact[]>(() => {
    if (
      formik.values.propertyContacts &&
      formik.values.propertyContacts.length > 0
    ) {
      return formik.values.propertyContacts.map(
        (contact: any, index: number) => ({
          id: contact.id || `contact_${index}`,
          name: contact.name || "",
          email: contact.email || "",
          phone: contact.phone || "",
          phoneType: normalizePropertyContactPhoneType(contact.phoneType),
          isPrimary: contact.isPrimary || index === 0,
        })
      );
    }
    return [
      {
        name: "",
        email: "",
        phone: "",
        phoneType: "",
        isPrimary: true,
      },
    ];
  });
  const [optionalPropertyDetails, setOptionalPropertyDetails] = useState<
    OptionalPropertyDetailsType[]
  >([]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetLocationsData());
    dispatch(getCountriesListRequest());
  }, []); //eslint-disable-line

  useEffect(() => {
    if (
      formik.values.propertyRecord?.optionalPropertyDetails &&
      formik.values.propertyRecord?.optionalPropertyDetails.length > 0
    ) {
      setOptionalPropertyDetails(
        formik.values.propertyRecord.optionalPropertyDetails
      );
    }
  }, [formik.values.propertyRecord]);

  const { countries, states, loading } = useSelector(
    (state: any) => state.location
  );

  const [selectedContact, setSelectedContact] = useState(() => {
    if (
      formik.values.propertyContacts &&
      formik.values.propertyContacts.length > 0
    ) {
      const primaryIndex = (formik.values.propertyContacts || []).findIndex(
        (contact: any) => contact.isPrimary
      );
      return `contact${primaryIndex >= 0 ? primaryIndex : 0}`;
    }
    return "contact0";
  });

  const addContact = () => {
    const newContacts = [
      ...contacts,
      {
        name: "",
        email: "",
        phone: "",
        phoneType: "",
        isPrimary: false,
      },
    ];
    setContacts(newContacts);
    formik.setFieldValue("propertyContacts", newContacts);
    formik.setFieldTouched("propertyContacts", true);
  };

  useEffect(() => {
    if (formik.values.propertyRecord?.country) {
      dispatch(
        getStatesByCountryRequest({
          country: formik.values.propertyRecord.country,
        })
      );
    }
  }, [formik.values.propertyRecord?.country]); //eslint-disable-line

  const removeContact = (index: number) => {
    if (contacts.length > 1) {
      const newContacts = contacts.filter((_, i) => i !== index);
      setContacts(newContacts);
      formik.setFieldValue("propertyContacts", newContacts);
      formik.setFieldTouched("propertyContacts", true);

      const selectedIndex = Number(
        selectedContact.substring(selectedContact.length - 1)
      );
      if (selectedIndex > index) {
        setSelectedContact(`contact${selectedIndex - 1}`);
      }

      setTimeout(() => {
        formik.validateField("propertyContacts");
      }, 0);
    }
  };

  const handleSelectedContactChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedContact(event.target.value);
    const newContacts = [...contacts];
    newContacts.forEach((contact, index) => {
      contact.isPrimary = index === selectedIndex;
    });
    setContacts(newContacts);
    formik.setFieldValue("propertyContacts", newContacts);
  };

  const updateContact = (
    index: number,
    field: keyof PropertyContact,
    value: any
  ) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setContacts(newContacts);
    formik.setFieldValue("propertyContacts", newContacts);
  };

  useEffect(() => {
    if (
      formik.values.propertyContacts &&
      formik.values.propertyContacts.length > 0
    ) {
      const mappedContacts = formik.values.propertyContacts.map(
        (contact: any, index: number) => ({
          id: contact.id || `contact_${index}`,
          name: contact.name || "",
          email: contact.email ?? "",
          phone: contact.phone || "",
          phoneType: normalizePropertyContactPhoneType(contact.phoneType),
          isPrimary: contact.isPrimary || index === 0,
        })
      );
      setContacts(mappedContacts);

      const primaryIndex = formik.values.propertyContacts.findIndex(
        (contact: any) => contact.isPrimary
      );
      setSelectedContact(`contact${primaryIndex >= 0 ? primaryIndex : 0}`);
    }
  }, [formik.values.propertyContacts]); //eslint-disable-line

  useEffect(() => {
    if ((formik.values.propertyContacts || []).length === 0) {
      formik.setFieldValue("propertyContacts", [
        {
          name: "",
          email: "",
          phone: "",
          phoneType: "",
          isPrimary: true,
        },
      ]);
    }
  }, [formik.values.propertyContacts]); //eslint-disable-line

  const addOptionalDetails = () => {
    const newOptionalDetails = [
      ...optionalPropertyDetails,
      {
        type: "",
        description: "",
      },
    ];
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue(
      "propertyRecord.optionalPropertyDetails",
      newOptionalDetails
    );
    formik.setFieldTouched("propertyRecord.optionalPropertyDetails", true);
    setTimeout(() => {
      formik.validateField("propertyRecord.optionalPropertyDetails");
    }, 0);
  };

  const updateOptionalDetailsType = (value: string, index: number) => {
    const newOptionalDetails = optionalPropertyDetails.map((item, i) =>
      i === index ? { ...item, type: value } : item
    );
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue(
      "propertyRecord.optionalPropertyDetails",
      newOptionalDetails
    );
  };

  const updateOptionalDetailsDescription = (value: string, index: number) => {
    const newOptionalDetails = optionalPropertyDetails.map((item, i) =>
      i === index ? { ...item, description: value } : item
    );
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue(
      "propertyRecord.optionalPropertyDetails",
      newOptionalDetails
    );
  };

  const removeOptionalDetails = (removeIndex: number) => {
    const newOptionalDetails = optionalPropertyDetails.filter(
      (_, i) => i !== removeIndex
    );
    setOptionalPropertyDetails(newOptionalDetails);
    formik.setFieldValue(
      "propertyRecord.optionalPropertyDetails",
      newOptionalDetails
    );
    formik.setFieldTouched("propertyRecord.optionalPropertyDetails", true);
    setTimeout(() => {
      formik.validateField("propertyRecord.optionalPropertyDetails");
    }, 0);
  };

  // API payload transformation handled in parent on submit

  // Submit handled by parent formik

  if (simplifiedView) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={labelAboveSx}>Loan #</Typography>
              <TextInput
                fullWidth
                name="propertyRecord.loanNumber"
                variant="outlined"
                size="small"
                value={formik.values.propertyRecord?.loanNumber ?? ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)?.loanNumber &&
                  Boolean((formik.errors.propertyRecord as any)?.loanNumber)
                }
                helperText={
                  (formik.touched.propertyRecord as any)?.loanNumber &&
                  (formik.errors.propertyRecord as any)?.loanNumber
                    ? String((formik.errors.propertyRecord as any)?.loanNumber)
                    : ""
                }
                disabled={disabled}
                sx={{ width: "100% !important", ...inputBgSx }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={labelAboveSx}>Property Name</Typography>
              <TextInput
                fullWidth
                name="propertyRecord.propertyName"
                variant="outlined"
                size="small"
                value={formik.values.propertyRecord?.propertyName ?? ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)?.propertyName &&
                  Boolean((formik.errors.propertyRecord as any)?.propertyName)
                }
                helperText={
                  (formik.touched.propertyRecord as any)?.propertyName &&
                  (formik.errors.propertyRecord as any)?.propertyName
                    ? String(
                        (formik.errors.propertyRecord as any)?.propertyName
                      )
                    : ""
                }
                disabled={disabled}
                sx={{ width: "100% !important", ...inputBgSx }}
              />
            </Grid>
          </Grid>

          <Typography sx={{ ...labelAboveSx, fontWeight: 600, mt: 2, mb: 1 }}>
            Address
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small" sx={inputBgSx}>
              <InputLabel>Country</InputLabel>
              <Select
                name="propertyRecord.country"
                value={formik.values.propertyRecord?.country ?? ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)?.country &&
                  Boolean((formik.errors.propertyRecord as any)?.country)
                }
                label="Country"
                disabled={disabled}
              >
                {(countries || [])?.map((country: any) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
              {(formik.touched.propertyRecord as any)?.country &&
                (formik.errors.propertyRecord as any)?.country && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {String((formik.errors.propertyRecord as any)?.country)}
                  </Typography>
                )}
            </FormControl>
            <TextInput
              fullWidth
              name="propertyRecord.street"
              label="Street Address"
              variant="outlined"
              size="small"
              value={formik.values.propertyRecord?.street ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.street &&
                Boolean((formik.errors.propertyRecord as any)?.street)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.street &&
                (formik.errors.propertyRecord as any)?.street
                  ? String((formik.errors.propertyRecord as any)?.street)
                  : ""
              }
              disabled={disabled}
              sx={{ width: "100% !important", ...inputBgSx }}
            />
            <TextInput
              fullWidth
              name="propertyRecord.address2"
              label="Address 2 (Optional)"
              variant="outlined"
              size="small"
              value={formik.values.propertyRecord?.address2 ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={disabled}
              sx={{ width: "100% !important", ...inputBgSx }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={labelAboveSx}>City</Typography>
                <TextInput
                  fullWidth
                  name="propertyRecord.city"
                  variant="outlined"
                  size="small"
                  value={formik.values.propertyRecord?.city ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.propertyRecord as any)?.city &&
                    Boolean((formik.errors.propertyRecord as any)?.city)
                  }
                  helperText={
                    (formik.touched.propertyRecord as any)?.city &&
                    (formik.errors.propertyRecord as any)?.city
                      ? String((formik.errors.propertyRecord as any)?.city)
                      : ""
                  }
                  disabled={disabled}
                  sx={{ width: "100% !important", ...inputBgSx }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={labelAboveSx}>State</Typography>
                <FormControl fullWidth size="small" sx={inputBgSx}>
                  <Select
                    name="propertyRecord.state"
                    value={formik.values.propertyRecord?.state ?? ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      (formik.touched.propertyRecord as any)?.state &&
                      Boolean((formik.errors.propertyRecord as any)?.state)
                    }
                    disabled={disabled}
                  >
                    {(states?.states || [])?.map((state: any) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                  {(formik.touched.propertyRecord as any)?.state &&
                    (formik.errors.propertyRecord as any)?.state && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5 }}
                      >
                        {String((formik.errors.propertyRecord as any)?.state)}
                      </Typography>
                    )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography sx={labelAboveSx}>Zip</Typography>
                <TextInput
                  fullWidth
                  name="propertyRecord.zip"
                  variant="outlined"
                  size="small"
                  value={formik.values.propertyRecord?.zip ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.propertyRecord as any)?.zip &&
                    Boolean((formik.errors.propertyRecord as any)?.zip)
                  }
                  helperText={
                    (formik.touched.propertyRecord as any)?.zip &&
                    (formik.errors.propertyRecord as any)?.zip
                      ? String((formik.errors.propertyRecord as any)?.zip)
                      : ""
                  }
                  disabled={disabled}
                  sx={{ width: "100% !important", ...inputBgSx }}
                />
              </Grid>
            </Grid>
          </Box>

          <Typography sx={{ ...labelAboveSx, fontWeight: 600, mt: 2, mb: 1 }}>
            Property Contact
          </Typography>
          <Typography sx={{ ...labelAboveSx, mb: 1 }}>Primary</Typography>
          {contacts.map((contact, index) => (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gridTemplateRows: "auto auto",
                gap: 2,
                mb: 2,
                alignItems: "start",
              }}
            >
              <Box sx={{ gridRow: 1, gridColumn: 1, pt: 0.5 }}>
                <Radio
                  sx={{ color: "primary.main", p: 0 }}
                  checked={selectedContact === `contact${index}`}
                  onChange={(e) => handleSelectedContactChange(e, index)}
                  value={`contact${index}`}
                  name="contact-radio"
                  disabled={disabled}
                />
              </Box>
              <Box
                sx={{ gridRow: 1, gridColumn: 2, minWidth: 0, width: "100%" }}
              >
                <TextInput
                  label="Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={contact.name}
                  onChange={(e) => updateContact(index, "name", e.target.value)}
                  onBlur={() => {
                    formik.setFieldTouched(
                      `propertyContacts.${index}.name`,
                      true
                    );
                    setTimeout(
                      () => formik.validateField("propertyContacts"),
                      0
                    );
                  }}
                  error={Boolean(
                    (formik.touched.propertyContacts?.[index] as any)?.name &&
                      (formik.errors.propertyContacts?.[index] as any)?.name
                  )}
                  helperText={
                    (formik.touched.propertyContacts?.[index] as any)?.name &&
                    (formik.errors.propertyContacts?.[index] as any)?.name
                      ? String(
                          (formik.errors.propertyContacts?.[index] as any)?.name
                        )
                      : ""
                  }
                  disabled={disabled}
                  sx={{ width: "100% !important", ...inputBgSx }}
                />
              </Box>
              <Box
                sx={{
                  gridRow: 1,
                  gridColumn: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  pt: 0.5,
                  width: "40px",
                }}
              >
                {!contact.isPrimary ? (
                  <IconButton
                    onClick={() => removeContact(index)}
                    disabled={disabled}
                    sx={{ color: "error.main", p: 0.5 }}
                    size="small"
                    aria-label="delete contact"
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
              <Box sx={{ gridRow: 2, gridColumn: 1 }} />
              <Box
                sx={{
                  gridRow: 2,
                  gridColumn: 2,
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 1fr",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <TextInput
                  label="Email"
                  variant="outlined"
                  size="small"
                  type="email"
                  value={contact.email}
                  onChange={(e) =>
                    updateContact(index, "email", e.target.value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched(
                      `propertyContacts.${index}.email`,
                      true
                    );
                    setTimeout(
                      () => formik.validateField("propertyContacts"),
                      0
                    );
                  }}
                  error={Boolean(
                    (formik.touched.propertyContacts?.[index] as any)?.email &&
                      (formik.errors.propertyContacts?.[index] as any)?.email
                  )}
                  helperText={
                    (formik.touched.propertyContacts?.[index] as any)?.email &&
                    (formik.errors.propertyContacts?.[index] as any)?.email
                      ? String(
                          (formik.errors.propertyContacts?.[index] as any)
                            ?.email
                        )
                      : ""
                  }
                  disabled={disabled}
                  fullWidth
                  style={{ width: "100%" }}
                  sx={{ minWidth: 0, ...inputBgSx }}
                />
                <TextInput
                  label="Phone"
                  variant="outlined"
                  size="small"
                  value={contact.phone}
                  onChange={(e) =>
                    updateContact(index, "phone", e.target.value)
                  }
                  onBlur={() => {
                    formik.setFieldTouched(
                      `propertyContacts.${index}.phone`,
                      true
                    );
                    setTimeout(
                      () => formik.validateField("propertyContacts"),
                      0
                    );
                  }}
                  error={Boolean(
                    (formik.touched.propertyContacts?.[index] as any)?.phone &&
                      (formik.errors.propertyContacts?.[index] as any)?.phone
                  )}
                  helperText={
                    (formik.touched.propertyContacts?.[index] as any)?.phone &&
                    (formik.errors.propertyContacts?.[index] as any)?.phone
                      ? String(
                          (formik.errors.propertyContacts?.[index] as any)
                            ?.phone
                        )
                      : ""
                  }
                  disabled={disabled}
                  fullWidth
                  style={{ width: "100%" }}
                  sx={{ minWidth: 0, ...inputBgSx }}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                />
                <FormControl
                  sx={{ width: "100%", minWidth: 0, ...inputBgSx }}
                  size="small"
                >
                  <InputLabel
                    id={`edit-property-phone-type-label-${index}`}
                    shrink
                  >
                    Phone Type
                  </InputLabel>
                  <Select
                    displayEmpty
                    labelId={`edit-property-phone-type-label-${index}`}
                    value={contact.phoneType ?? ""}
                    onChange={(e) =>
                      updateContact(index, "phoneType", e.target.value)
                    }
                    onBlur={() => {
                      formik.setFieldTouched(
                        `propertyContacts.${index}.phoneType`,
                        true
                      );
                      setTimeout(
                        () => formik.validateField("propertyContacts"),
                        0
                      );
                    }}
                    label="Phone Type"
                    disabled={disabled}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="Mobile">Mobile</MenuItem>
                    <MenuItem value="Office">Office</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ gridRow: 2, gridColumn: 3 }} />
            </Box>
          ))}
          {contacts.length < 4 && (
            <Button
              startIcon={<Add sx={{ width: 18, height: 18 }} />}
              onClick={addContact}
              disabled={disabled}
              sx={{
                color: "primary.main",
                fontSize: 13,
                alignItems: "center",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <b>ADD ADDITIONAL</b>
            </Button>
          )}

          <OptionalPropertyDetails
            optionalPropertyDetails={optionalPropertyDetails}
            formik={formik}
            addOptionalDetails={addOptionalDetails}
            updateOptionalDetailsType={updateOptionalDetailsType}
            updateOptionalDetailsDescription={updateOptionalDetailsDescription}
            removeOptionalDetails={removeOptionalDetails}
            disabled={disabled}
          />

          {loading && <Loading />}
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Loan #</Typography>
            <TextInput
              className="field-input"
              name="propertyRecord.loanNumber"
              variant="outlined"
              value={formik.values.propertyRecord.loanNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.loanNumber &&
                Boolean((formik.errors.propertyRecord as any)?.loanNumber)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.loanNumber &&
                (formik.errors.propertyRecord as any)?.loanNumber
                  ? String((formik.errors.propertyRecord as any)?.loanNumber)
                  : ""
              }
              disabled={disabled}
              sx={{ width: "calc(100% - 180px)" }}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Account #</Typography>
            <TextInput
              className="field-input"
              name="propertyRecord.accountNumber"
              variant="outlined"
              value={formik.values.propertyRecord.accountNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.accountNumber &&
                Boolean((formik.errors.propertyRecord as any)?.accountNumber)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.accountNumber &&
                (formik.errors.propertyRecord as any)?.accountNumber
                  ? String((formik.errors.propertyRecord as any)?.accountNumber)
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Reference #</Typography>
            <TextInput
              className="field-input"
              name="propertyRecord.referenceNumber"
              variant="outlined"
              value={formik.values.propertyRecord.referenceNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.referenceNumber &&
                Boolean((formik.errors.propertyRecord as any)?.referenceNumber)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.referenceNumber &&
                (formik.errors.propertyRecord as any)?.referenceNumber
                  ? String(
                      (formik.errors.propertyRecord as any)?.referenceNumber
                    )
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Property Name</Typography>
            <TextInput
              className="field-input"
              name="propertyRecord.propertyName"
              variant="outlined"
              value={formik.values.propertyRecord.propertyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.propertyName &&
                Boolean((formik.errors.propertyRecord as any)?.propertyName)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.propertyName &&
                (formik.errors.propertyRecord as any)?.propertyName
                  ? String((formik.errors.propertyRecord as any)?.propertyName)
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">
              Collateral/Property
            </Typography>
            <Box
              sx={{
                width: "calc(100% - 180px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TextInput
                fullWidth
                className="field-input"
                name="propertyRecord.collateralOrPropertyType"
                label="Type"
                variant="outlined"
                multiline
                rows={1}
                value={formik.values.propertyRecord.collateralOrPropertyType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)
                    ?.collateralOrPropertyType &&
                  Boolean(
                    (formik.errors.propertyRecord as any)
                      ?.collateralOrPropertyType
                  )
                }
                helperText={
                  (formik.touched.propertyRecord as any)
                    ?.collateralOrPropertyType &&
                  (formik.errors.propertyRecord as any)
                    ?.collateralOrPropertyType
                    ? String(
                        (formik.errors.propertyRecord as any)
                          ?.collateralOrPropertyType
                      )
                    : ""
                }
                sx={{ width: "100% !important" }}
                disabled={disabled}
              />
              <TextInput
                fullWidth
                className="field-input"
                name="propertyRecord.collateralOrPropertyDescription"
                label="Description"
                variant="outlined"
                multiline
                rows={1}
                value={
                  formik.values.propertyRecord.collateralOrPropertyDescription
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)
                    ?.collateralOrPropertyDescription &&
                  Boolean(
                    (formik.errors.propertyRecord as any)
                      ?.collateralOrPropertyDescription
                  )
                }
                helperText={
                  (formik.touched.propertyRecord as any)
                    ?.collateralOrPropertyDescription &&
                  (formik.errors.propertyRecord as any)
                    ?.collateralOrPropertyDescription
                    ? String(
                        (formik.errors.propertyRecord as any)
                          ?.collateralOrPropertyDescription
                      )
                    : ""
                }
                sx={{ width: "100% !important", mt: 2 }}
                disabled={disabled}
              />
            </Box>
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Year Built</Typography>
            <InputNumber
              className="field-input"
              name="propertyRecord.yearBuilt"
              label="Year Built"
              variant="outlined"
              value={formik.values.propertyRecord.yearBuilt}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.yearBuilt &&
                Boolean((formik.errors.propertyRecord as any)?.yearBuilt)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.yearBuilt &&
                (formik.errors.propertyRecord as any)?.yearBuilt
                  ? String((formik.errors.propertyRecord as any)?.yearBuilt)
                  : ""
              }
              allowDecimal={false}
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Year Renovated</Typography>
            <InputNumber
              className="field-input"
              name="propertyRecord.yearRenovated"
              label="Year Renovated"
              variant="outlined"
              value={formik.values.propertyRecord.yearRenovated}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.yearRenovated &&
                Boolean((formik.errors.propertyRecord as any)?.yearRenovated)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.yearRenovated &&
                (formik.errors.propertyRecord as any)?.yearRenovated
                  ? String((formik.errors.propertyRecord as any)?.yearRenovated)
                  : ""
              }
              allowDecimal={false}
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">
              Name of Business
            </Typography>
            <TextInput
              className="field-input"
              name="propertyRecord.nameOfBusiness"
              label="Name of Business"
              variant="outlined"
              value={formik.values.propertyRecord.nameOfBusiness}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.nameOfBusiness &&
                Boolean((formik.errors.propertyRecord as any)?.nameOfBusiness)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.nameOfBusiness &&
                (formik.errors.propertyRecord as any)?.nameOfBusiness
                  ? String(
                      (formik.errors.propertyRecord as any)?.nameOfBusiness
                    )
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="field-label-txt">Business Phone</Typography>
            <TextInput
              className="field-input"
              name="propertyRecord.businessPhone"
              label="Business Phone"
              variant="outlined"
              value={formik.values.propertyRecord.businessPhone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.businessPhone &&
                Boolean((formik.errors.propertyRecord as any)?.businessPhone)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.businessPhone &&
                (formik.errors.propertyRecord as any)?.businessPhone
                  ? String((formik.errors.propertyRecord as any)?.businessPhone)
                  : ""
              }
              sx={{ width: "calc(100% - 180px)" }}
              disabled={disabled}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Address</Typography>
          <Box
            sx={{
              width: "calc(100% - 180px)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <FormControl>
              <InputLabel>Country</InputLabel>
              <Select
                name="propertyRecord.country"
                value={formik.values.propertyRecord.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)?.country &&
                  Boolean((formik.errors.propertyRecord as any)?.country)
                }
                label="Country"
                disabled={disabled}
              >
                {(countries || [])?.map((country: any) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
              {(formik.touched.propertyRecord as any)?.country &&
                (formik.errors.propertyRecord as any)?.country && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, ml: 1 }}
                  >
                    {String((formik.errors.propertyRecord as any)?.country)}
                  </Typography>
                )}
            </FormControl>
            <TextInput
              className="field-input"
              name="propertyRecord.street"
              label="Street Address"
              variant="outlined"
              value={formik.values.propertyRecord.street}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.street &&
                Boolean((formik.errors.propertyRecord as any)?.street)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.street &&
                (formik.errors.propertyRecord as any)?.street
                  ? String((formik.errors.propertyRecord as any)?.street)
                  : ""
              }
              sx={{ width: "100% !important" }}
              disabled={disabled}
            />

            <TextInput
              className="field-input"
              name="propertyRecord.address2"
              label="Address 2 (Optional)"
              variant="outlined"
              value={formik.values.propertyRecord.address2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.address2 &&
                Boolean((formik.errors.propertyRecord as any)?.address2)
              }
              helperText={
                (formik.touched.propertyRecord as any)?.address2 &&
                (formik.errors.propertyRecord as any)?.address2
                  ? String((formik.errors.propertyRecord as any)?.address2)
                  : ""
              }
              sx={{ width: "100% !important" }}
              disabled={disabled}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextInput
                sx={{ flex: 1 }}
                className="field-input"
                name="propertyRecord.city"
                label="City"
                variant="outlined"
                value={formik.values.propertyRecord.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)?.city &&
                  Boolean((formik.errors.propertyRecord as any)?.city)
                }
                helperText={
                  (formik.touched.propertyRecord as any)?.city &&
                  (formik.errors.propertyRecord as any)?.city
                    ? String((formik.errors.propertyRecord as any)?.city)
                    : ""
                }
                disabled={disabled}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>State</InputLabel>
                <Select
                  name="propertyRecord.state"
                  value={formik.values.propertyRecord.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formik.touched.propertyRecord as any)?.state &&
                    Boolean((formik.errors.propertyRecord as any)?.state)
                  }
                  label="State"
                  disabled={disabled}
                >
                  {(states?.states || [])?.map((state: any) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
                {(formik.touched.propertyRecord as any)?.state &&
                  (formik.errors.propertyRecord as any)?.state && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 1, ml: 1 }}
                    >
                      {String((formik.errors.propertyRecord as any)?.state)}
                    </Typography>
                  )}
              </FormControl>
              <TextInput
                sx={{ flex: 1 }}
                className="field-input"
                name="propertyRecord.zip"
                label="Zip"
                variant="outlined"
                value={formik.values.propertyRecord.zip}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.propertyRecord as any)?.zip &&
                  Boolean((formik.errors.propertyRecord as any)?.zip)
                }
                helperText={
                  (formik.touched.propertyRecord as any)?.zip &&
                  (formik.errors.propertyRecord as any)?.zip
                    ? String((formik.errors.propertyRecord as any)?.zip)
                    : ""
                }
                disabled={disabled}
              />
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Number of Units</Typography>
          <InputNumber
            className="field-input"
            name="propertyRecord.numberOfUnits"
            variant="outlined"
            value={formik.values.propertyRecord.numberOfUnits}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.propertyRecord as any)?.numberOfUnits &&
              Boolean((formik.errors.propertyRecord as any)?.numberOfUnits)
            }
            helperText={
              (formik.touched.propertyRecord as any)?.numberOfUnits &&
              (formik.errors.propertyRecord as any)?.numberOfUnits
                ? String((formik.errors.propertyRecord as any)?.numberOfUnits)
                : ""
            }
            allowDecimal={false}
            sx={{ width: "calc(100% - 180px)" }}
            disabled={disabled}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">
            Number of Buildings
          </Typography>
          <InputNumber
            className="field-input"
            name="propertyRecord.numberOfBuildings"
            variant="outlined"
            value={formik.values.propertyRecord.numberOfBuildings}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.propertyRecord as any)?.numberOfBuildings &&
              Boolean((formik.errors.propertyRecord as any)?.numberOfBuildings)
            }
            helperText={
              (formik.touched.propertyRecord as any)?.numberOfBuildings &&
              (formik.errors.propertyRecord as any)?.numberOfBuildings
                ? String(
                    (formik.errors.propertyRecord as any)?.numberOfBuildings
                  )
                : ""
            }
            allowDecimal={false}
            sx={{ width: "calc(100% - 180px)" }}
            disabled={disabled}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Investor Number</Typography>
          <TextInput
            className="field-input"
            name="propertyRecord.investorNumber"
            variant="outlined"
            value={formik.values.propertyRecord.investorNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.propertyRecord as any)?.investorNumber &&
              Boolean((formik.errors.propertyRecord as any)?.investorNumber)
            }
            helperText={
              (formik.touched.propertyRecord as any)?.investorNumber &&
              (formik.errors.propertyRecord as any)?.investorNumber
                ? String((formik.errors.propertyRecord as any)?.investorNumber)
                : ""
            }
            sx={{ width: "calc(100% - 180px)" }}
            disabled={disabled}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Square Footage</Typography>
          <InputNumber
            className="field-input"
            name="propertyRecord.squareFootage"
            variant="outlined"
            value={formik.values.propertyRecord.squareFootage}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.propertyRecord as any)?.squareFootage &&
              Boolean((formik.errors.propertyRecord as any)?.squareFootage)
            }
            helperText={
              (formik.touched.propertyRecord as any)?.squareFootage &&
              (formik.errors.propertyRecord as any)?.squareFootage
                ? String((formik.errors.propertyRecord as any)?.squareFootage)
                : ""
            }
            allowDecimal={false}
            sx={{ width: "calc(100% - 180px)" }}
            disabled={disabled}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">O&M Required?</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={
                  formik.values.propertyRecord.operationsAndMaintenanceRequired
                }
                onChange={(e) =>
                  formik.setFieldValue(
                    "propertyRecord.operationsAndMaintenanceRequired",
                    e.target.checked
                  )
                }
                sx={{
                  color: "primary.main",
                  "&.Mui-checked": {
                    color: "primary.main",
                  },
                }}
              />
            }
            label={
              formik.values.operationsAndMaintenanceRequired ? "Yes" : "No"
            }
            sx={{ width: "calc(100% - 180px)" }}
            disabled={disabled}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Loan Balance</Typography>
          <Box sx={{ display: "flex", gap: 2, width: "calc(100% - 180px)" }}>
            <InputNumber
              fullWidth
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              className="field-input"
              name="propertyRecord.loanBalance"
              variant="outlined"
              value={formik.values.propertyRecord.loanBalance}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.propertyRecord as any)?.loanBalance &&
                Boolean((formik.errors.propertyRecord as any)?.loanBalance)
              }
              disabled={disabled}
              sx={{ flex: 1, width: "calc(100% - 180px)" }}
            />
            <DatePicker
              label="As of"
              value={
                formik.values.propertyRecord.loanBalanceAsOf
                  ? dayjs(formik.values.propertyRecord.loanBalanceAsOf)
                  : null
              }
              onChange={(date) => {
                const dateValue = date?.format("YYYY-MM-DD");
                formik.setFieldValue(
                  "propertyRecord.loanBalanceAsOf",
                  dateValue
                );
                formik.setFieldTouched("propertyRecord.loanBalanceAsOf", true);
                setTimeout(() => {
                  formik.validateField("propertyRecord.loanBalanceAsOf");
                }, 0);
              }}
              format="MMMM D, YYYY"
              slotProps={{
                inputAdornment: {
                  position: "start",
                },
                textField: {
                  variant: "outlined",
                  placeholder: "Select Date",
                  error:
                    (formik.touched.propertyRecord as any)?.loanBalanceAsOf &&
                    Boolean(
                      (formik.errors.propertyRecord as any)?.loanBalanceAsOf
                    ),
                  helperText:
                    (formik.touched.propertyRecord as any)?.loanBalanceAsOf &&
                    (formik.errors.propertyRecord as any)?.loanBalanceAsOf
                      ? String(
                          (formik.errors.propertyRecord as any)?.loanBalanceAsOf
                        )
                      : "",
                },
              }}
              sx={{ flex: 1 }}
              disabled={disabled}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="field-label-txt">Property Contact</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>Primary</Typography>
            {contacts.map((contact, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "start", mt: 2 }}
              >
                <Radio
                  sx={{ mr: 2, color: "primary.main" }}
                  checked={selectedContact === `contact${index}`}
                  onChange={(e) => handleSelectedContactChange(e, index)}
                  value={`contact${index}`}
                  name="contact-radio"
                  disabled={disabled}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flex: 1,
                  }}
                >
                  <TextInput
                    className="field-input"
                    name={`propertyContacts.${index}.name`}
                    label="Name"
                    variant="outlined"
                    value={contact.name}
                    onChange={(e) =>
                      updateContact(index, "name", e.target.value)
                    }
                    onBlur={() => {
                      formik.setFieldTouched(
                        `propertyContacts.${index}.name`,
                        true
                      );
                      setTimeout(() => {
                        formik.validateField(`propertyContacts.${index}.name`);
                      }, 0);
                    }}
                    error={Boolean(
                      (formik.touched.propertyContacts?.[index] as any)?.name &&
                        (formik.errors.propertyContacts?.[index] as any)?.name
                    )}
                    helperText={
                      (formik.touched.propertyContacts?.[index] as any)?.name &&
                      (formik.errors.propertyContacts?.[index] as any)?.name
                        ? String(
                            (formik.errors.propertyContacts?.[index] as any)
                              ?.name
                          )
                        : ""
                    }
                    sx={{ width: "100% !important" }}
                    disabled={disabled}
                  />

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextInput
                      className="field-input"
                      name={`propertyContacts.${index}.email`}
                      label="Email"
                      variant="outlined"
                      type="email"
                      value={contact.email}
                      onChange={(e) =>
                        updateContact(index, "email", e.target.value)
                      }
                      onBlur={() => {
                        formik.setFieldTouched(
                          `propertyContacts.${index}.email`,
                          true
                        );
                        setTimeout(() => {
                          formik.validateField(
                            `propertyContacts.${index}.email`
                          );
                        }, 0);
                      }}
                      error={Boolean(
                        (formik.touched.propertyContacts?.[index] as any)
                          ?.email &&
                          (formik.errors.propertyContacts?.[index] as any)
                            ?.email
                      )}
                      helperText={
                        (formik.touched.propertyContacts?.[index] as any)
                          ?.email &&
                        (formik.errors.propertyContacts?.[index] as any)?.email
                          ? String(
                              (formik.errors.propertyContacts?.[index] as any)
                                ?.email
                            )
                          : ""
                      }
                      sx={{ flex: 1 }}
                      disabled={disabled}
                    />
                    <TextInput
                      className="field-input"
                      name={`propertyContacts.${index}.phone`}
                      label="Phone"
                      variant="outlined"
                      value={contact.phone}
                      onChange={(e) =>
                        updateContact(index, "phone", e.target.value)
                      }
                      onBlur={() => {
                        formik.setFieldTouched(
                          `propertyContacts.${index}.phone`,
                          true
                        );
                        setTimeout(() => {
                          formik.validateField(
                            `propertyContacts.${index}.phone`
                          );
                        }, 0);
                      }}
                      error={Boolean(
                        (formik.touched.propertyContacts?.[index] as any)
                          ?.phone &&
                          (formik.errors.propertyContacts?.[index] as any)
                            ?.phone
                      )}
                      helperText={
                        (formik.touched.propertyContacts?.[index] as any)
                          ?.phone &&
                        (formik.errors.propertyContacts?.[index] as any)?.phone
                          ? String(
                              (formik.errors.propertyContacts?.[index] as any)
                                ?.phone
                            )
                          : ""
                      }
                      sx={{ flex: 1 }}
                      disabled={disabled}
                      slotProps={{ htmlInput: { maxLength: 50 } }}
                    />
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel
                        id={`edit-property-phone-type-full-label-${index}`}
                        shrink
                      >
                        Phone Type
                      </InputLabel>
                      <Select
                        displayEmpty
                        labelId={`edit-property-phone-type-full-label-${index}`}
                        value={contact.phoneType ?? ""}
                        onChange={(e) =>
                          updateContact(index, "phoneType", e.target.value)
                        }
                        onBlur={() => {
                          formik.setFieldTouched(
                            `propertyContacts.${index}.phoneType`,
                            true
                          );
                          setTimeout(() => {
                            formik.validateField(
                              `propertyContacts.${index}.phoneType`
                            );
                          }, 0);
                        }}
                        label="Phone Type"
                        disabled={disabled}
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                        <MenuItem value="Mobile">Mobile</MenuItem>
                        <MenuItem value="Office">Office</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                {!contact.isPrimary ? (
                  <IconButton
                    onClick={() => removeContact(index)}
                    disabled={disabled}
                    sx={{ color: "error.main", ml: 1 }}
                  >
                    <DeleteOutline />
                  </IconButton>
                ) : (
                  <Box sx={{ width: 46 }}></Box>
                )}
              </Box>
            ))}
            {contacts.length < 4 && (
              <Button
                startIcon={<Add />}
                onClick={addContact}
                disabled={disabled}
                sx={{
                  color: "primary.main",
                  textTransform: "none",
                  fontWeight: "bold",
                  mt: 2,
                }}
              >
                ADD ADDITIONAL
              </Button>
            )}
          </Box>
        </Box>
        {loading && <Loading />}
      </Box>
    </LocalizationProvider>
  );
}
